import React, { FC, useState } from 'react'
import { CButton } from 'tabs/data/components/CButton'
import * as S from './CreateDataset.style'
import { Dataset, DatasetType } from '../../../../../api/generated'
import { TextField } from '@mui/material'
import { Autocomplete } from '@mui/lab'

const maxNameLength = 100
const maxDescriptionLength = 1000

export interface DatasetFormProps {
  datasetId: string
  // name: string
  // versionLabel: string
  allDatasets: Dataset[]
  onSubmit: (
    name: string,
    description: string,
    mandatoryDataPoints: string,
    type: DatasetType,
    powerBiId?: string,
    cloneFromId?: string,
    versionLabel?: string,
  ) => Promise<void>
  onCancel?: () => void
}

type CloneOption = {
  id: string
  name: string
  versionLabel: string | null
}

const datasetTypeOptions = [
  { label: 'SQL', value: DatasetType.Sql },
  { label: 'POWER BI', value: DatasetType.PowerBi },
] as const

type DatasetTypeOption = typeof datasetTypeOptions[number]

export const CreateDataset: FC<DatasetFormProps> = ({ allDatasets, onSubmit, onCancel }) => {
  const [loading, setLoading] = useState(false)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [mandatoryDataPoints, setMandatoryDataPoints] = useState('')
  const [powerBiId, setPowerBiId] = useState(null)
  const [type, setType] = useState<DatasetType | null>(null)
  const [versionLabel, setVersionLabel] = useState('')

  const [errorName, setErrorName] = useState('')
  const [errorDescription, setErrorDescription] = useState('')
  const [errorDataPoints, setErrorDataPoints] = useState('')
  const [errorType, setErrorType] = useState('')
  const [errorVersionLabel, setErrorVersionLabel] = useState('')
  const [errorPowerBiId, setErrorPowerBiId] = useState('')
  const [errorAPI, setErrorAPI] = useState('')

  const [selectedCloneSource, setSelectedCloneSource] = useState<CloneOption | null>(null)
  const [cloneInputValue, setCloneInputValue] = useState('')
  const [selectedType, setSelectedType] = useState<DatasetTypeOption | null>(null)

  const cloneOptions: CloneOption[] = allDatasets.flatMap(dataset =>
    dataset.allDatasetVersions.map(version => ({
      id: version.datasetId,
      name: dataset.datasetName || '',
      versionLabel: version.datasetVersionLabel || '',
    })),
  )

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setName(value)
    if (value.length > maxNameLength) {
      setErrorName(`Max length is ${maxNameLength} characters.`)
    } else if (!value.trim()) {
      setErrorName('Name is required.')
    } else {
      setErrorName('')
    }
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setDescription(value)
    if (value.length > maxDescriptionLength) {
      setErrorDescription(`Max length is ${maxDescriptionLength} characters.`)
    } else if (!value.trim()) {
      setErrorDescription('Description is required.')
    } else {
      setErrorDescription('')
    }
  }

  const handleDataPointsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setMandatoryDataPoints(value)

    if (value.length > maxDescriptionLength) {
      setErrorDataPoints(`Max length is ${maxDescriptionLength} characters.`)
    } else {
      setErrorDataPoints('')
    }
  }

  const handleReset = () => {
    setName('')
    setDescription('')
    setMandatoryDataPoints('')
    setPowerBiId('')
    setType(null)
    setSelectedType(null)
    setSelectedCloneSource(null)
    setVersionLabel('')
    if (onCancel) onCancel()
  }

  const handleSubmit = async () => {
    let valid = true

    if (!name.trim()) {
      setErrorName('Name is required.')
      valid = false
    } else {
      setErrorName('')
    }

    if (!description.trim()) {
      setErrorDescription('Description is required.')
      valid = false
    } else {
      setErrorDescription('')
    }

    if (!selectedType) {
      setErrorType('Type is required.')
      valid = false
    } else {
      setErrorType('')
    }

    if (selectedType?.value === DatasetType.PowerBi && !powerBiId?.trim()) {
      setErrorPowerBiId('Power BI ID is required for POWER BI type.')
      valid = false
    } else {
      setErrorPowerBiId('')
    }

    if (selectedCloneSource && !versionLabel?.trim()) {
      setErrorVersionLabel('Version label is required when cloning.')
      valid = false
    } else {
      setErrorVersionLabel('')
    }

    if (!valid || loading) return

    try {
      setLoading(true)
      await onSubmit(
        name.trim(),
        description.trim(),
        mandatoryDataPoints?.trim(),
        selectedType.value,
        powerBiId?.trim(),
        selectedCloneSource?.id,
        versionLabel?.trim(),
      )
      if (onCancel) onCancel()
    } catch (e) {
      throw e
    } finally {
      setLoading(false)
    }
  }

  return (
    <S.Container>
      <S.Form>
        <S.Title>Name*</S.Title>
        <S.InputContainer isError={!!errorName}>
          <S.TitleInput
            value={name}
            onChange={handleNameChange}
            disabled={loading}
            placeholder="The name must exactly match the name used in Power BI"
          />
        </S.InputContainer>
        <S.Error>{errorName}</S.Error>

        <S.Title>Description*</S.Title>
        <S.InputContainer isError={!!errorDescription}>
          <S.Textarea
            value={description}
            onChange={handleDescriptionChange}
            disabled={loading}
            placeholder="Dataset description"
            isError={!!errorDescription}
          />
        </S.InputContainer>
        <S.Error>{errorDescription}</S.Error>

        <S.Title>
          Mandatory question data points <small style={{ fontWeight: 400 }}>(optional)</small>
        </S.Title>
        <S.InputContainer isError={!!errorDataPoints}>
          <S.Textarea
            value={mandatoryDataPoints}
            onChange={handleDataPointsChange}
            disabled={loading}
            placeholder="+ Insert question mandatory data points here (e.g. Date, Region, Category)"
            isError={!!errorDataPoints}
          />
        </S.InputContainer>
        <S.Error>{errorDataPoints}</S.Error>

        <S.Title>Dataset Type*</S.Title>
        <Autocomplete
          options={datasetTypeOptions}
          getOptionLabel={(option: DatasetTypeOption) => option.label}
          value={selectedType}
          onChange={(_e, newValue) => {
            setSelectedType(newValue)
            setType(newValue?.value)
          }}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="Select type..."
              size="small"
              fullWidth
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                style: { borderRadius: 12 },
              }}
            />
          )}
          style={{ width: '100%' }}
        />
        <S.Error>{errorType}</S.Error>

        {type === DatasetType.PowerBi && (
          <>
            <S.Title>Power BI ID*</S.Title>
            <S.InputContainer>
              <S.TitleInput
                value={powerBiId}
                onChange={e => {
                  setPowerBiId(e.target.value)
                  if (errorPowerBiId) setErrorPowerBiId('')
                }}
                disabled={loading}
                placeholder="Power BI dataset ID"
              />
            </S.InputContainer>
          </>
        )}
        <S.Error>{errorPowerBiId}</S.Error>

        <S.Title>
          Clone from <small style={{ fontWeight: 400 }}>(optional)</small>
        </S.Title>
        <Autocomplete
          options={cloneOptions}
          getOptionLabel={(option: CloneOption) => `${option.name} (${option?.versionLabel})`}
          value={selectedCloneSource}
          inputValue={cloneInputValue}
          onInputChange={(_e, newInputValue) => setCloneInputValue(newInputValue)}
          onChange={(_e, newValue) => setSelectedCloneSource(newValue)}
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Select dataset version to clone from..."
              InputProps={{
                ...params.InputProps,
                style: { borderRadius: 12 },
              }}
            />
          )}
          style={{ width: '100%' }}
        />

        {selectedCloneSource && (
          <>
            <S.Title>New version label*</S.Title>
            <TextField
              placeholder="e.g. v1.1, release-2025"
              size="small"
              fullWidth
              value={versionLabel}
              onChange={e => {
                setVersionLabel(e.target.value)
                if (errorVersionLabel) setErrorVersionLabel('')
              }}
              disabled={loading}
              variant="outlined"
              InputProps={{
                style: { borderRadius: 12 },
              }}
              style={{ width: '100%' }}
            />
          </>
        )}
        <S.Error>{errorVersionLabel}</S.Error>

        <S.Error>{errorAPI}</S.Error>

        <S.Footer>
          <CButton color="secondary" onClick={handleReset} disabled={loading}>
            Cancel
          </CButton>
          <CButton onClick={handleSubmit} disabled={loading}>
            Create Dataset
          </CButton>
        </S.Footer>
      </S.Form>
    </S.Container>
  )
}
